import { queryFleetProducts } from '@/api/visual'
import { getChartDevice } from '@/api/device-chart'
import { createRequestCancellation, isRequestCanceled } from '@/api/http'
import { responseBody } from './device-chart-data'
import { isStaticDataPreview, sharedReadOptions } from './shared-view'
import { pageTextSources, pageTextSettings } from './page-context-text-model'

const states = new WeakMap()
const text = (value, fallback = '') => typeof value === 'string' ? value : fallback
export const usesPageContextText = vm => Number(vm.dataType) !== 6 && vm.$options.name === 'avue-echart-text' && vm.option && pageTextSources.includes(vm.option.textSource)
const editing = host => !!host.menuFlag && !host.isPreview

export function pageProductTextKey (vm, host) {
  const option = vm.option || {}
  return JSON.stringify([host.navigationContext, option.textSource, option.textPrefix, option.textSuffix,
    option.contextDataSource, option.contextExamples,
    pageTextSettings(option), editing(host), isStaticDataPreview(host),
    !!host.isSharedView, !!host.isSharedDataView, !!host.isComponentTemplateEditing, !!host.isTemplateEditing])
}

function stateOf (vm) {
  if (!states.has(vm)) states.set(vm, { sequence: 0 })
  return states.get(vm)
}

function paint (vm, name, status, error = '') {
  const option = vm.option || {}, state = stateOf(vm)
  vm.dataChart = { value: text(option.textPrefix) + name + text(option.textSuffix) }
  if (!vm.$el) return
  vm.$el.setAttribute('aria-busy', String(status === 'loading'))
  vm.$el.setAttribute('data-page-product-state', status)
  if (error) {
    if (!state.hasErrorTitle) state.previousTitle = vm.$el.getAttribute('title')
    state.hasErrorTitle = true
    vm.$el.setAttribute('title', error)
  } else restoreTitle(vm, state)
}

function restoreTitle (vm, state) {
  if (!state.hasErrorTitle || !vm.$el) return
  if (state.previousTitle === null) vm.$el.removeAttribute('title')
  else vm.$el.setAttribute('title', state.previousTitle)
  state.hasErrorTitle = false
}

export function stopPageProductText (vm) {
  const state = stateOf(vm)
  state.sequence++
  clearTimeout(state.timer); state.timer = null
  if (state.request) state.request.cancel('页面联动文本已切换')
  state.request = null
  restoreTitle(vm, state)
  if (vm.$el) {
    vm.$el.removeAttribute('aria-busy')
    vm.$el.removeAttribute('data-page-product-state')
  }
}

export async function updatePageProductText (vm, host) {
  stopPageProductText(vm)
  const state = stateOf(vm), sequence = state.sequence, binding = pageProductTextKey(vm, host)
  const current = () => !vm._isDestroyed && usesPageContextText(vm) && sequence === state.sequence && binding === pageProductTextKey(vm, host)
  const settings = pageTextSettings(vm.option), placeholder = settings.empty
  // 显式演示模式只取保存的名称映射，不请求示例编号对应的真实设备；真实来源从不回退演示值。
  if (vm.option.contextDataSource === 'static') {
    const id = (host.navigationContext || {})[settings.contextKey]
    const examples = vm.option.contextExamples
    const value = settings.identifier && typeof id === 'string' ? id
      : examples && typeof examples === 'object' && !Array.isArray(examples) && Object.prototype.hasOwnProperty.call(examples, id) && typeof examples[id] === 'string' ? examples[id] : settings.example
    paint(vm, value, 'demo')
    return
  }
  // 编辑示例不进入运行查询；模板不请求真实产品或设备，不能用示例冒充运行结果。
  if (editing(host) || host.isComponentTemplateEditing || host.isTemplateEditing || isStaticDataPreview(host)) {
    paint(vm, settings.example, 'example')
    return
  }
  const id = (host.navigationContext || {})[settings.contextKey]
  if (id == null || id === '') { paint(vm, placeholder, 'pending'); return }
  if (typeof id !== 'string' || !/^[A-Za-z0-9_-]{1,64}$/.test(id)) {
    paint(vm, placeholder, 'error', '页面传入的' + (settings.device ? '设备' : '产品') + ' ID 无效')
    return
  }
  // 编号直接取本次导航的 deviceId，不再查询一次设备详情。
  if (settings.identifier) { paint(vm, id, 'ready'); return }
  paint(vm, placeholder, 'loading')
  try {
    state.request = createRequestCancellation()
    // 复用现有只读入口，登录与分享均沿用各自权限，不开放新的分享地址。
    const options = sharedReadOptions(host, { cancelToken: state.request.token })
    const query = { paging: true, pageIndex: 0, pageSize: 1, terms: [{ column: 'id', termType: 'eq', value: id }] }
    const body = responseBody(await (settings.device ? getChartDevice(id, options) : queryFleetProducts(query, options)))
    if (!current()) return
    const result = body.result
    const entity = settings.device ? result : result && Array.isArray(result.data) && result.total === 1 && result.data.length === 1 ? result.data[0] : null
    if (!entity || entity.id !== id || typeof entity.name !== 'string' || !entity.name.trim()) {
      throw new Error((settings.device ? '设备' : '产品') + '不存在、无权访问或名称返回无效')
    }
    paint(vm, entity.name, 'ready')
  } catch (error) {
    if (current() && !isRequestCanceled(error)) paint(vm, placeholder, 'error', error.message || settings.label + '读取失败')
  } finally { if (current()) state.request = null }
}

export function queuePageProductText (vm, host) {
  stopPageProductText(vm)
  if (vm.sub) { try { vm.sub.unsubscribe() } catch (_) { /* 已关闭的旧自定义订阅 */ } vm.sub = null }
  clearInterval(vm.checkChart)
  paint(vm, pageTextSettings(vm.option).empty, 'pending')
  const state = stateOf(vm)
  state.timer = setTimeout(() => updatePageProductText(vm, host), 100)
}
